import { ANNOT_ENDE } from '../domain/abgeleitet';
import { kompakteKennung, namensBudget } from '../domain/dateiname';

import { appendAppLog } from './appLog';
import { APP_VERSION, geraeteName } from './appVersion';
import {
  DateiFehlt,
  liesBase64,
  liesText,
  listeOrdner,
  loescheDatei,
  schreibeBase64,
  schreibeText,
} from './dateien';
import { kopiereBildNachFotos, kopiereSvgNachFotos } from './fotos';
import { merkeOrdnerFehlt, ordnerFehlt, stelleOrdnerSicher } from './ordner';
import { sigPfad } from './signature';
import { signFile } from './signing';

/**
 * Ablage der Mediendateien auf dem Gerät.
 *
 * Vorgabe des Nutzers: alles liegt im **Documents-Ordner** unter `logs/<Bezug>/<Art>` — erst
 * immer `logs`, dann wo der Eintrag hängt, dann was es ist. Der Dateiname trägt die Zuordnung
 * als Schlüssel-Wert-Paare, damit eine Datei auch ohne Datenbank noch lesbar bleibt.
 *
 * ⚠️ **Der Documents-Ordner ist seit dem 2026-09-11 über iTunes/Finder freigegeben**
 * (`UIFileSharingEnabled` im `Info.plist`, Entscheidung des Nutzers). Vorher war er
 * zugesperrt, weil es Prüfungsdaten sind; jetzt kommt jeder heran, der das iPad an einen
 * Rechner steckt — dafür lassen sich Belege ohne Umweg über den Server holen. Die
 * **Dateien-App** bleibt aussen vor: `LSSupportsOpeningDocumentsInPlace` steht weiterhin
 * nicht dort, im geführten Zugriff ist also nach wie vor kein Weg an die Dateien.
 */

/** Woran ein Eintrag hängt. `station` trägt nur Bildschirmfotos, es ist kein Kommentar. */
export type LogScope = 'station' | 'station-comment' | 'task-comment';

/** Was für eine Datei es ist. */
export type LogKind = 'screenshot' | 'photo' | 'audio' | 'video' | 'handwriting';

/** Die Zuordnung, die in den Dateinamen wandert. */
export interface FileSubject {
  examId: string;
  candidateId: string;
  examinerId: string;
  /**
   * Die Station.
   *
   * ⚠️ **Sie steht seit dem 2026-09-14 immer im Dateinamen** (Vorgabe des Nutzers), nicht mehr
   * nur bei Stationskommentaren. Der Ordner `logs/station/…` trennt zwar schon, aber der
   * Server liest die Zuordnung aus dem **Namen** — er müsste die Station sonst über die
   * Aufgabe nachschlagen. Im Manifest der Beglaubigung stand sie ohnehin immer.
   */
  stationId?: string;
  /** Der Schauspieler, sofern die Station einen führt. */
  actorId?: string;
  /** Fehlt bei Einträgen auf Stationsebene. */
  taskId?: string;
  /**
   * Teilnummer, 1-basiert, für Aufnahmen die aus mehreren Dateien bestehen — ein Bogen, der
   * nicht auf einen Schirm passt. Fehlt, wenn es nur eine Datei gibt.
   */
  part?: number;
}

export const logFolder = (scope: LogScope, kind: LogKind) => 'logs/' + scope + '/' + kind;

/**
 * Zeitanteil des Dateinamens: `yyyy-MM-dd-HH-mm-ss-SSS`, also mit Millisekunden
 * (vom Nutzer am 2026-09-08 bestätigt).
 */
export function timestampPart(now = new Date()): string {
  const p = (v: number, n = 2) => String(v).padStart(n, '0');
  return (
    now.getFullYear() +
    '-' + p(now.getMonth() + 1) +
    '-' + p(now.getDate()) +
    '-' + p(now.getHours()) +
    '-' + p(now.getMinutes()) +
    '-' + p(now.getSeconds()) +
    '-' + p(now.getMilliseconds(), 3)
  );
}

/**
 * Welche Begriffe der Dateiname für Station und Aufgabe führt.
 *
 * ⚠️ **Das Gleis entscheidet** (Vorgabe des Nutzers, 2026-09-17): Relay bekommt die Namen
 * seines Datenmodells, der Classic-Server seine eigenen. Beide meinen dasselbe — der `ExamItem`
 * heisst in Classic „Task", der `ExamItemContainer` ist unsere Station.
 *
 * ⚠️ **Seit dem 2026-09-21 in der kurzen Schreibweise** (`domain/dateiname.ts`): die
 * ausgeschriebenen Schlüssel sprengten zusammen mit den UUIDs die Namensgrenze von 255 Bytes,
 * und iOS wies jeden Schreibversuch ab. Gemeint ist weiter dasselbe.
 *
 * | Gleis | Station | Aufgabe |
 * | --- | --- | --- |
 * | `relay` | `cont` (`examItemContainerID`) | `item` (`examItemID`) |
 * | `classic` | `stat` (`stationID`) | `task` (`taskID`) |
 */
export type Namensform = 'relay' | 'classic';

const FELDER: Record<Namensform, { station: string; aufgabe: string }> = {
  relay: { station: 'cont', aufgabe: 'item' },
  classic: { station: 'stat', aufgabe: 'task' },
};

/**
 * Das eingestellte Gleis — ein Merker im Modul, kein Parameter.
 *
 * ⚠️ **Warum nicht durchgereicht.** `buildFileName` ist synchron und steht am Ende jeder
 * Aufnahme (Foto, Video, Ton, Handschrift, Bogenabzug); der Modus dagegen liegt in den
 * Einstellungen und wird asynchron gelesen (`server/zugang.ts`). Ihn durch jede Aufrufkette zu
 * fädeln hiesse, ihn an einem Dutzend Stellen nicht zu vergessen. Dieselbe Bauform wie
 * `persistence/examLog.ts::setDeviceInfo`.
 *
 * ⚠️ **Vorgabe ist Relay** — das ist der Normalbetrieb; Classic ist der Sonderfall, und er wird
 * eingestellt, bevor eine Verbindung steht.
 */
let namensform: Namensform = 'relay';

/** Setzt das Gleis; gerufen aus `ladeZugang` und `speichereZugang`. */
export function setzeNamensform(form: Namensform): void {
  namensform = form;
}

/** Nur für Tests und Anzeigen — welche Begriffe gerade geschrieben werden. */
export const aktuelleNamensform = (): Namensform => namensform;

/**
 * Baut den Dateinamen (Aufbau vom Nutzer vorgegeben, 2026-09-14; Begriffe je Gleis seit
 * 2026-09-17):
 *
 * ```
 * <art>_date=…_exam=…_cand=…_exmr=…_actr=…_cont=…_item=….<endung>
 * <art>_date=…_exam=…_cand=…_exmr=…_actr=…_stat=…_task=….<endung>
 * ```
 *
 * ⚠️ **Kurze Schlüssel und kompakte Kennungen seit dem 2026-09-21** (`domain/dateiname.ts`).
 * Die ausgeschriebene Form kam mit sechs UUIDs auf 282 bis 330 Zeichen und lag damit über der
 * Namensgrenze von 255 Bytes: iOS wies **jede** Aufnahme ab („The item couldn't be saved
 * because the file name … is invalid", `OS-PLUG-FILE-0013`). Weder Foto noch Video liessen
 * sich ablegen, in beiden Gleisen. Jetzt sind es 207 Zeichen im vollen Fall.
 *
 * ⚠️ **Der Name ist die Zuordnung.** Seit dem 2026-09-14 trägt der `value` der Protokollzeile
 * nur noch die **Anzahl** der Aufnahmen (`domain/medienzahl.ts`); die Zuordnung einer Datei
 * steht allein in ihrem Namen — auch die Felder der Übertragung werden daraus gelesen
 * (`server/anhaenge.ts::baueAnhangFelder`). Was hier fehlt, kann niemand nachschlagen; deshalb
 * stehen Station, Schauspieler und die Art mit darin.
 *
 * ⚠️ **Keine Aufgabe, kein Feld.** Beim Stationskommentar fehlt das Aufgabenfeld ganz, statt
 * einen Platzhalter zu tragen: Abwesenheit **ist** die Aussage, und ein Sonderwert wie `-1`
 * müsste auf der Gegenseite eigens abgefangen werden (mit der Relay-Sitzung so vereinbart,
 * 2026-09-17). Die Station steht immer da — eine Aufnahme ohne Station gibt es im OSCE nicht.
 *
 * ⚠️ **Ältere Dateien auf dem Gerät tragen andere Formen** (ausgeschriebene Schlüssel, volle
 * UUIDs, ohne Art, ohne `actorID`, mit `itemID` neben `taskID`, mit `stationID` nur beim
 * Stationskommentar) — und nach einem Moduswechsel liegen beide Gleise nebeneinander in
 * derselben Ablage. Jeder **Leser** muss deshalb alle Formen vertragen; das steht an einer
 * Stelle für alle (`domain/dateiname.ts::felderAusTeilen`, gerufen aus
 * `server/multipart.ts::felderAusName` und `persistence/repos.ts::dateiname`).
 *
 * Werte werden von `_` und `=` befreit — beides trennt hier und würde den Namen sonst
 * unlesbar machen.
 */
export function buildFileName(
  kind: LogKind | 'overlay',
  subject: FileSubject,
  extension: string,
  now = new Date(),
): string {
  const clean = (v: string) => v.replace(/[_=]/g, '-');
  /** Eine Kennung, wie sie im Namen steht: kompakt, und ohne unsere Trennzeichen. */
  const wert = (v: string) => clean(kompakteKennung(v));
  /*
   * ⚠️ **Die Art steht voran und trägt kein `=`** (Vorgabe des Nutzers, 2026-09-14). Sie ist
   * damit auf den ersten Blick lesbar — im Dateimanager wie im Ablageordner des Servers —, und
   * jeder Leser, der den Namen an `_` zerlegt und Teile ohne `=` überspringt
   * (`server/multipart.ts::felderAusName`, `persistence/repos.ts::dateiname`), übergeht sie
   * von selbst. Die Art steht zusätzlich im Ordnerpfad und als eigenes Feld der Anfrage.
   */
  const parts = [
    kind + '_date=' + timestampPart(now),
    'exam=' + wert(subject.examId),
    'cand=' + wert(subject.candidateId),
  ];
  /*
   * ⚠️ **Die Station steht seit dem 2026-09-14 immer im Namen**, auch neben einer Aufgabe.
   * Vorher stand sie nur dort, wo keine Aufgabe war (beim Stationskommentar); die Zuordnung
   * musste sie sonst über die Aufgabe nachschlagen. Gelesen wird aber allein der Name — was
   * hier fehlt, kann niemand ergänzen.
   *
   * ⚠️ **Ein Begriff je Gleis, nicht zwei.** Bis zum 2026-09-17 standen `taskID` und `itemID`
   * mit demselben Wert nebeneinander, damit jeder Leser fand, wonach er suchte. Mit der
   * Trennung nach Gleis hat jede Seite ihren eigenen Namen, und ein drittes Feld wäre nur eine
   * weitere Schreibweise derselben Sache — drei Felder, eine Wahrheit, und beim nächsten Leser
   * die Frage, welches gilt. `itemID` ist deshalb entfallen (Entscheidung des Nutzers).
   *
   * `opfer` sagt, in welcher Reihenfolge ein Feld weichen müsste, wenn der Name trotz der
   * kompakten Kennungen zu lang würde (s. `kuerzeAuf`) — der Schauspieler zuerst, die Station
   * zuletzt. Was nicht in dieser Liste steht, bleibt unter allen Umständen: ohne `date` und
   * `cand` findet `persistence/repos.ts::dateiname` die Datei nicht mehr, ohne `exam`
   * verfehlt sie das Archiv (`platform/archive.ts::gehoertZurPruefung`).
   */
  const feld = FELDER[namensform];
  const zusatz: { text: string; opfer: number }[] = [];
  if (subject.actorId) zusatz.push({ text: 'actr=' + wert(subject.actorId), opfer: 1 });
  if (subject.examinerId) zusatz.push({ text: 'exmr=' + wert(subject.examinerId), opfer: 2 });
  if (subject.taskId) {
    zusatz.push({ text: feld.aufgabe + '=' + wert(subject.taskId), opfer: 3 });
  }
  if (subject.stationId) {
    zusatz.push({ text: feld.station + '=' + wert(subject.stationId), opfer: 4 });
  }

  // Die Reihenfolge im Namen ist die gewohnte, nicht die der Opferliste: Prüfer, Schauspieler,
  // Station, Aufgabe. Wer einen Namen liest, soll ihn überall gleich aufgebaut vorfinden.
  const reihenfolge = ['exmr=', 'actr=', feld.station + '=', feld.aufgabe + '='];
  zusatz.sort((a, b) => stelleIn(reihenfolge, a.text) - stelleIn(reihenfolge, b.text));

  // Mehrteilige Aufnahmen (ein Bogen, der nicht auf einen Schirm passt) brauchen eine
  // Unterscheidung, die nicht die Uhr ist: zwei Abzüge können in dieselbe Millisekunde
  // fallen, und dann überschriebe der zweite den ersten. Die Nummer sagt zugleich, in
  // welcher Reihenfolge die Teile zusammengehören.
  const schluss = subject.part !== undefined ? ['part=' + String(subject.part)] : [];

  return kuerzeAuf(parts, zusatz, schluss, extension);
}

/** Wo ein Feld in der Namensreihenfolge steht; Unbekanntes hinten. */
function stelleIn(reihenfolge: string[], teil: string): number {
  const i = reihenfolge.findIndex((p) => teil.startsWith(p));
  return i < 0 ? reihenfolge.length : i;
}

/**
 * Setzt den Namen zusammen und hält ihn unter der Namensgrenze.
 *
 * ⚠️ **Eine Wache, keine Rechnung, die immer aufgeht.** Mit unseren UUIDs kommt der volle Name
 * auf 207 Zeichen und die Wache greift nie. Sie steht hier für den Fall, den es am 2026-09-21
 * am Gerät gab: Kennungen wachsen, und das Dateisystem lehnt den Schreibversuch dann **ganz**
 * ab — eine Aufnahme, die nicht entsteht, ist in einer Prüfung der schlimmste Ausgang.
 * Lieber ein Beleg mit einem Feld weniger als gar keiner; was fehlt, steht im Dateilog.
 *
 * Gekürzt wird feldweise nach `opfer`, nie mitten in einer Kennung: eine halbe UUID sähe aus
 * wie eine gültige und zeigte auf niemanden.
 */
function kuerzeAuf(
  pflicht: string[],
  zusatz: { text: string; opfer: number }[],
  schluss: string[],
  extension: string,
): string {
  const bauen = (teile: { text: string; opfer: number }[]) =>
    [...pflicht, ...teile.map((z) => z.text), ...schluss].join('_') + '.' + extension;

  let behalten = zusatz;
  let name = bauen(behalten);
  const gefallen: string[] = [];

  while (laenge(name) > namensBudget() && behalten.length > 0) {
    const naechstes = behalten.reduce((a, b) => (a.opfer <= b.opfer ? a : b));
    gefallen.push(naechstes.text.slice(0, naechstes.text.indexOf('=')));
    behalten = behalten.filter((z) => z !== naechstes);
    name = bauen(behalten);
  }

  if (gefallen.length > 0) {
    void appendAppLog(
      'Dateiname zu lang (Grenze ' + String(namensBudget()) + ' Zeichen): ' +
        gefallen.join(', ') + ' weggelassen bei ' + name,
      'warn',
    ).catch(() => undefined);
  }
  /*
   * ⚠️ **Auch das Pflichtteil kann zu lang sein** — dann hilft kein Weglassen mehr, und die
   * Ablage wird scheitern wie am 2026-09-21. Gekürzt wird trotzdem nicht: eine halbe Kennung
   * sähe aus wie eine gültige. Stattdessen steht es vorher im Log, statt erst als Fehler des
   * Dateisystems hinterher.
   */
  if (laenge(name) > namensBudget()) {
    void appendAppLog(
      'Dateiname bleibt über der Grenze (' + String(laenge(name)) + ' von ' +
        String(namensBudget()) + ' Bytes), die Ablage wird ihn abweisen: ' + name,
      'error',
    ).catch(() => undefined);
  }
  return name;
}

/**
 * Die Länge in **Bytes**, nicht in Zeichen — das Dateisystem zählt Bytes, und ein Umlaut in
 * einer Kennung aus dem Classic-Server zählt in UTF-8 doppelt.
 */
const laenge = (text: string): number => new TextEncoder().encode(text).length;

/**
 * Die zuletzt vergebene Zeitmarke je Ordner, in Millisekunden — die Wache gegen zwei Dateien
 * mit demselben Namen.
 *
 * Am 2026-09-10 am Simulator nachgemessen: drei gleichzeitige Schreibvorgänge derselben Art
 * fielen in zwei Millisekunden, zwei bekamen denselben Namen, und einer überschrieb den
 * anderen **lautlos**. Im heutigen Ablauf kommt das nicht vor — die App schreibt nie zwei
 * gleichartige Dateien desselben Prüflings gleichzeitig —, aber eine Ablage, die Daten still
 * verliert, sobald es doch einmal geschieht, ist der falsche Untergrund für Prüfungsdaten.
 *
 * Kein Blick ins Dateisystem, sondern ein Gedächtnis im Modul: der Fall entsteht nur innerhalb
 * **eines** Laufs, und ein `stat` je Schreibvorgang kostete auf einem älteren iPad mehr, als es
 * bringt.
 */
const letzteZeit = new Map<string, number>();

/**
 * Wie `buildFileName`, aber mit **streng steigender** Zeitmarke je Ordner: fällt ein zweiter
 * Schreibvorgang in dieselbe Millisekunde, rückt er eine weiter.
 *
 * Streng steigend und nicht bloss „anders als der letzte": bei drei gleichzeitigen Vorgängen
 * fiele der dritte sonst auf die Marke des ersten zurück — genau die Kollision, die hier
 * verhindert werden soll.
 */
function freierName(
  folder: string,
  kind: LogKind | 'overlay',
  subject: FileSubject,
  extension_: string,
): string {
  const vorige = letzteZeit.get(folder);
  const ms = vorige === undefined ? Date.now() : Math.max(Date.now(), vorige + 1);
  letzteZeit.set(folder, ms);
  return buildFileName(kind, subject, extension_, new Date(ms));
}

const ensureFolder = stelleOrdnerSicher;

/**
 * Legt eine Datei ab und gibt ihren Pfad unterhalb von Documents zurück.
 *
 * @param data Base64 ohne `data:`-Vorspann.
 */
export async function saveBase64(
  scope: LogScope,
  kind: LogKind,
  subject: FileSubject,
  extension: string,
  data: string,
): Promise<string> {
  const folder = logFolder(scope, kind);
  await ensureFolder(folder);
  const path = folder + '/' + freierName(folder, kind, subject, extension);
  await schreibeBase64(path, data);
  await beglaubige(path, subject);
  // Zweitschrift in die Fotos-Mediathek, wenn die Einrichtung es eingeschaltet hat. Bewusst
  // **nach** dem Beglaubigen und ohne `await`: der Beleg ist zu diesem Zeitpunkt geschrieben
  // und gültig, und ein Prüfer soll auf eine Bequemlichkeit nicht warten. Der Aufruf wirft
  // nicht (s. `fotos.ts`).
  void kopiereBildNachFotos(kind, subject, extension, data);
  return path;
}

/** Textdatei ablegen — für die Handschrift, die als SVG gesichert wird. */
export async function saveText(
  scope: LogScope,
  kind: LogKind,
  subject: FileSubject,
  extension: string,
  text: string,
): Promise<string> {
  const folder = logFolder(scope, kind);
  await ensureFolder(folder);
  const path = folder + '/' + freierName(folder, kind, subject, extension);
  await schreibeText(path, text);
  await beglaubige(path, subject);
  // Auch die Handschrift ist eine Grafik. Sie liegt als SVG vor; scheitert das Zeichnen auf
  // der Leinwand (ältere WebViews sind damit eigenwillig), bleibt es bei der Zeile im
  // Dateilog — der Beleg selbst ist davon unberührt.
  if (kind === 'handwriting') {
    void kopiereSvgNachFotos(kind, subject, text);
  }
  return path;
}

/**
 * Die Markierungsebene heisst wie ihr Foto, mit `-overlay.svg` dahinter.
 *
 * Damit steht die Zuordnung im Namen: das Zurückprojizieren beim Öffnen ist ein Anhängen,
 * kein Nachschlagen. Angehängt wird an den **vollen** Namen samt Endung — sonst hiessen die
 * Ebenen zu `foto.jpeg` und `foto.png` gleich.
 */
export const overlayPfad = (fotoPfad: string) => fotoPfad + '-overlay.svg';

/**
 * Wo die zeitlich begrenzte Markierungsebene einer Videoaufnahme liegt
 * (`domain/videoAnnotation.ts`).
 *
 * ⚠️ **JSON, nicht SVG** — anders als beim Foto. Der Relay-Server soll daraus seine eigene
 * Ausgabe erzeugen und bekommt deshalb die Daten, kein fertiges Markup (Entscheidung des
 * Nutzers, 2026-09-20). Die Endung steht in `domain/abgeleitet.ts`.
 */
export const annotationsPfad = (videoPfad: string) => videoPfad + ANNOT_ENDE;

/**
 * Wie `saveText`, aber unter **vorgegebenem** Namen.
 *
 * Die Markierungsebene darf keine eigene Zeitmarke bekommen: sie gehört zu genau einem Foto
 * und wird bei jeder Änderung überschrieben. Ein neuer Name je Fassung würde den Bezug zum
 * Bild kappen und den Ordner mit Altfassungen füllen.
 *
 * Sie läuft durch dieselbe `beglaubige`-Schleuse wie jede andere Ablage — sonst gäbe es einen
 * zweiten Schreibweg daran vorbei, und die Ebene läge unsigniert neben einem signierten Foto.
 */
export async function saveTextAs(
  path: string,
  text: string,
  subject: FileSubject,
): Promise<string> {
  await ensureFolder(path.slice(0, path.lastIndexOf('/')));
  await schreibeText(path, text);
  await beglaubige(path, subject);
  return path;
}

/**
 * Jede fertig geschriebene Datei bekommt ihre Beglaubigung — hier, an der einen Stelle, durch
 * die alle Ablagen laufen. Eine Datei, die jemand später hinzufügt, ist damit von selbst
 * beglaubigt; das ist der Grund, warum es nicht an den Aufrufstellen steht.
 *
 * `subject` trägt bereits Prüfung, Prüfling und Prüfer — genau die Angaben, die ins Manifest
 * gehören. Mehr braucht es nicht.
 */
async function beglaubige(path: string, subject: FileSubject): Promise<void> {
  try {
    await signFile(
      path,
      {
        deviceName: geraeteName(),
        examId: subject.examId,
        stationId: subject.stationId ?? '',
        candidateId: subject.candidateId,
        examinerId: subject.examinerId,
        appVersion: APP_VERSION,
      },
      appendAppLog,
    );
  } catch (fehler) {
    /*
     * ⚠️ **Der Fehler sagt jetzt, an welcher der beiden Hälften es lag** (seit 2026-09-24).
     *
     * `saveText`/`saveBase64` tun zweierlei: die Datei schreiben und sie beglaubigen. Scheitert
     * eines von beiden, wirft der ganze Aufruf — und der Aufrufer sah bisher nur „konnte nicht
     * gesichert werden". Ob die Datei fehlt oder bloss ihre Unterschrift, ist aber ein
     * gewaltiger Unterschied: im zweiten Fall **liegt der Beleg da**.
     */
    // ⚠️ Ohne `cause`: das zweite Argument von `Error` gibt es erst ab ES2022, und die Ziellage
    // ist Safari 15 (`tools/pruefe-ios15.mjs` wacht darüber). Die Ursache steht deshalb im Text.
    throw new Error('Beglaubigung fehlgeschlagen (die Datei selbst liegt): ' + String(fehler));
  }
}

/**
 * Listet die abgelegten Dateien einer Art — für die Zuordnung von Aufnahmen zu Prüfling und
 * Aufgabe über den Dateinamen (`repos.ts`), ohne eigene Tabelle dafür.
 *
 * Ein noch nicht angelegter Ordner ist der Normalfall (noch keine Aufnahme dieser Art) und
 * liefert deshalb eine leere Liste statt eines Fehlers — wie `ensureFolder` oben.
 *
 * ⚠️ **Gefragt wird je Ordner nur einmal.** Das `catch` fängt den Fehler zwar ab, aber die
 * Capacitor-Brücke hat den abgelehnten Aufruf da längst in die Xcode-Konsole geschrieben —
 * dasselbe Übel, das `ordner.ts` beim `mkdir` schon abstellt. Diese Funktion läuft bei jedem
 * Bildaufbau der Medienreiter; am 2026-09-21 am Gerät gesehen: tausende Zeilen
 * `OS-PLUG-FILE-0008` auf dieselben vier Ordner, und der Fehler, den wir suchten, ging darin
 * unter. Der Merker in `ordner.ts` hält den Befund fest und wird von jedem gelungenen
 * Anlegen wieder aufgehoben.
 *
 * ⚠️ **Nur „gibt es nicht" wird gemerkt.** Ein Lesefehler aus anderem Grund (Zugriff, E/A)
 * darf den Ordner nicht für den Rest der Sitzung als leer gelten lassen — er kann Dateien
 * enthalten, die wir dann übergingen.
 */
export async function listFiles(scope: LogScope, kind: LogKind): Promise<string[]> {
  const folder = logFolder(scope, kind);
  if (ordnerFehlt(folder)) return [];
  try {
    const { dateien } = await listeOrdner(folder);
    return dateien.map((name) => folder + '/' + name);
  } catch (fehler) {
    if (fehler instanceof DateiFehlt) merkeOrdnerFehlt(folder);
    return [];
  }
}

/**
 * Liest eine Textdatei als Zeichenkette zurück.
 *
 * **Als Text lesen, nicht über `readBase64` und `atob`.** Zwei Gründe, beide im Browser
 * nachgemessen (2026-09-10):
 *
 * 1. Der Web-Rückfall von `@capacitor/filesystem` gibt eine als Text geschriebene Datei
 *    **nicht** als Base64 zurück. `atob` bekommt dann keinen gültigen Base64-Text und
 *    wirft — das Zurücklesen scheiterte lautlos, obwohl die Datei einwandfrei dalag.
 * 2. Selbst wo Base64 zurückkommt, macht `atob` aus jedem Byte ein Zeichen. Ein „ö" steht in
 *    UTF-8 auf zwei Bytes und käme so als zwei Zeichen Kauderwelsch zurück.
 *
 * Die Fassade (`platform/dateien.ts`) hält beide Türen getrennt, damit die Wahl nicht mehr
 * an einem mitgegebenen `encoding` hängt.
 */
export async function readText(path: string): Promise<string> {
  return liesText(path);
}

/** Liest eine Datei als Base64 zurück — für die Vorschau im Medien-Betrachter. */
export async function readBase64(path: string): Promise<string> {
  return liesBase64(path);
}

/**
 * Löscht eine Datei.
 *
 * ⚠️ **Nur „gibt es nicht" bleibt stumm** (schon weg oder nie geschrieben — für den Aufrufer
 * dasselbe Ergebnis); alles andere (Schreibschutz, volle Platte, E/A-Fehler) fliegt weiter,
 * sonst verschwindet ein echter Löschfehler spurlos und eine „gelöschte" Datei käme beim
 * nächsten Lesen zurück. Welcher Fehler das ist, entscheidet die Fassade — s. `DateiFehlt`
 * in `platform/dateien.ts`.
 */
export async function deleteFile(path: string): Promise<void> {
  try {
    await loescheDatei(path);
  } catch (fehler) {
    if (!(fehler instanceof DateiFehlt)) throw fehler;
  }
}

/**
 * Löscht eine Datei **samt ihrer Beglaubigung**.
 *
 * Ohne das bliebe neben jeder gelöschten Aufnahme eine `.sig` liegen, die auf eine Datei zeigt,
 * die es nicht mehr gibt: eine Bescheinigung über nichts, und beim Übertragen ein Anhang ohne
 * Inhalt. Geschrieben wird die Beglaubigung an einer einzigen Stelle (`beglaubige`), gelöscht
 * gehört sie deshalb auch an einer einzigen.
 *
 * Die Reihenfolge ist nicht beliebig: **erst die Datei, dann die Signatur.** Scheitert das
 * Löschen der Datei, bleibt ihre Beglaubigung stehen — eine Datei ohne Signatur wäre sonst
 * genau die Beweislage, die die Beglaubigung verhindern soll. Umgekehrt ist eine übrig
 * gebliebene Signatur ohne Datei nur Ballast.
 */
export async function deleteFileMitSignatur(path: string): Promise<void> {
  await deleteFile(path);
  await deleteFile(sigPfad(path));
}
